import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'ru' | 'ua';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getPath: (path: string) => string;
};
const translations = {
  en: {
    // Navigation
    'nav.getStarted': 'Free consultation',
    'nav.home': 'Home',
    'nav.services': 'Pricing',
    'nav.howItWorks': 'How it works',
    'nav.referral': 'Referral Program',
    'nav.privacy': 'Privacy Policy',
    'nav.terms': 'Terms of Service',
    'nav.pricing': 'Pricing',
    'nav.faq': 'FAQ',
    'nav.testimonials': 'Reviews',
    // Cookie Banner
    'cookie.title': 'We use cookies',
    'cookie.description': 'We use cookies to improve your experience on our site, analyze traffic, and personalize content.',
    'cookie.accept': 'Accept All',
    'cookie.decline': 'Decline',
    'cookie.learnMore': 'Learn More',
    // Hero - H1 tag
    'hero.badge': 'Professional web agency',
    'hero.title': "We'll build your website for free.",
    'hero.titleHighlight': 'You only pay $15/mo.',
    'hero.subtitle': "First the mockup — then the decision. If you don't like it — you leave without paying.",
    'hero.bullet1': 'Free website preview before payment',
    'hero.bullet2': '$15/mo — website maintenance fee if you like the result',
    'hero.bullet3': 'We work worldwide (fully remote)',
    'hero.cta': 'Free consultation',
    'hero.referral': 'Referral Program',
    'hero.receipt.title': 'Your receipt',
    'hero.receipt.design': 'Web Design',
    'hero.receipt.dev': 'Development',
    'hero.receipt.support': 'Support & Hosting',
    'hero.receipt.total': 'Total',
    'hero.receipt.save': 'SAVE $800',
    'hero.pill.dev': '$0 for development',
    'hero.pill.host': 'Hosting included',
    'hero.pill.edit': 'Unlimited edits',
    // Pricing - H2: Pricing & terms
    'pricing.title': 'Pricing',
    'pricing.titleHighlight': '& terms',
    'pricing.subtitle': 'First a free mockup. Payment only if everything suits you.',
    // H3: Free website preview
    'pricing.demo.title': 'Free website mockup',
    'pricing.demo.label': 'FREE',
    'pricing.demo.desc': "We'll show you your future website before payment.",
    'pricing.demo.b1': 'Mockup tailored to your business',
    'pricing.demo.b2': 'You can request changes',
    'pricing.demo.b3': "Don't like it — don't pay",
    'pricing.demo.cta': 'Free consultation',
    // H3: Website development ($249 one-time)
    'pricing.launch.title': 'Your turnkey website',
    'pricing.launch.price': '$15',
    'pricing.launch.period': '/ month',
    'pricing.launch.desc': 'Development is free. You only pay to keep the website running.',
    'pricing.launch.b1': 'Custom design',
    'pricing.launch.b2': 'Mobile adaptation',
    'pricing.launch.b3': 'Changes on request',
    'pricing.launch.b4': 'Lead form',
    'pricing.launch.cta': 'Free consultation',
    // How It Works - H2
    'how.title': 'How it',
    'how.titleHighlight': 'works',
    // H3 steps
    'how.step1.title': 'Tell us about your business',
    'how.step1.note': 'Takes 2 minutes — what you do and what website you need',
    'how.step2.title': 'We create a free website preview',
    'how.step2.note': 'We show the design and structure. You get a link in 1–3 days',
    'how.step3.title': 'You tell us what to change',
    'how.step3.note': 'Text, sections, style — we update everything',
    'how.step4.title': 'Connect for $15/mo — and your website is live',
    'how.step4.note': 'Development is already included. You only pay for hosting and support',
    'how.step5.title': 'We connect your domain and publish the website',
    'how.step5.note': 'Your website goes live and starts collecting inquiries',
    'how.note': "If you don't like the website — you don't pay.",
    // FAQ - H2: Frequently Asked Questions
    'faq.title': 'Frequently Asked',
    'faq.titleHighlight': 'Questions',
    'faq.subtitle': 'Everything you need to know about working with our team',
    // H3 questions
    'faq.q1': 'Why is the website free?',
    'faq.a1': "We earn on maintenance, not development. You pay $15/mo — and it's profitable for us to make high-quality websites so clients stay for a long time.",
    'faq.q2': 'What is included in $15 a month?',
    'faq.a2': "Hosting, domain, technical support, and revisions on request. The website works, receives requests — you don't worry about anything.",
    'faq.q3': 'Do I pay $15 forever?',
    'faq.a3': 'Yes, as long as the website is running. You can cancel anytime. If you cancel — the website goes offline, but you keep all the materials.',
    'faq.q4': "What if I don't like the website?",
    'faq.a4': 'Just leave. The mockup is free, revisions are free. You only start paying when you say "everything is perfect".',
    'faq.q5': 'Can I request changes after launch?',
    'faq.a5': "Yes. Revisions are included in the $15/mo. Just write to us — we'll fix it.",
    'faq.q6': 'What do I get at the start?',
    'faq.a6': 'A ready-to-use website with custom design, mobile adaptation, and a lead form. Connected domain and hosting. Everything turnkey.',
    // Testimonials - H2: What clients say
    'testimonials.title': 'Client',
    'testimonials.titleHighlight': 'Reviews',
    'testimonials.subtitle': 'What clients say about working with us',
    'testimonials.r1.name': 'Sarah',
    'testimonials.r1.text': 'I liked that they showed me the website for free first. I asked for a couple of changes, everything was fixed quickly. I subscribed for $15 a month — and the website started working immediately.',
    'testimonials.r1.business': 'Beauty specialist',
    'testimonials.r2.name': 'Alex',
    'testimonials.r2.text': 'They made a clear website for my services. First they showed me the mockup, then made changes. I pay $15 a month — and everything works.',
    'testimonials.r2.business': 'Local handyman',
    'testimonials.r3.name': 'James',
    'testimonials.r3.text': "It's convenient that you don't have to pay upfront. First I looked at the website, asked to simplify the text, then connected for $15 a month. Everything is fair and clear.",
    'testimonials.r3.business': 'Psychologist',
    // Consultation - H2: Get your free preview
    'consultation.title': 'Order a free',
    'consultation.titleHighlight': 'consultation',
    'consultation.subtitle': "Tell us what you need — we'll send a preview link.",
    'consultation.benefit1': 'Free preview',
    'consultation.benefit2': 'Fast delivery',
    'consultation.benefit3': 'No commitment',
    'consultation.name': 'Name',
    'consultation.namePlaceholder': 'Your name',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'your@email.com',
    'consultation.desc': 'Project description',
    'consultation.descPlaceholder': 'Tell us about your business and what kind of website you need...',
    'consultation.cta': 'Order a free consultation',
    'consultation.note': "No commitment. If you don't like the website — you don't pay.",
    'consultation.sending': 'Sending...',
    'consultation.or': 'or talk to our team directly',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Services',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    'footer.aboutLink': 'About Jetly',
    // Stats
    'stats.websites': 'Websites Launched',
    'stats.clients': 'Happy Clients',
    'stats.years': 'Years Experience',
    'stats.title': 'Our',
    'stats.titleHighlight': 'Impact',
    'stats.subtitle': 'Delivering results for businesses worldwide',
    // AI Course Page
    'course.hero.badge': 'Become a digital business owner',
    'course.hero.title': 'Online course on creating websites with AI',
    'course.hero.pill1': 'No experience needed',
    'course.hero.pill2': 'Quick ROI',
    'course.hero.pill3': 'Work for yourself',
    'course.hero.socialProof': 'students already earning with AI',
    'course.hero.socialProofCount': '100+',
    'course.hero.specialPrice': 'Special price',
    'course.hero.buyCourse': 'Buy course',
    'course.recognize.title': 'Do you recognize yourself?',
    'course.recognize.c1.title': 'Working for someone else — but could work for yourself',
    'course.recognize.c1.text': "Every day you give your time and energy to someone else's business. But you still haven't started your own.",
    'course.recognize.c2.title': "Want more money — but don't know how",
    'course.recognize.c2.text': "Your salary isn't growing. Freelancing seems scary. It feels like making money online requires special knowledge.",
    'course.recognize.c3.title': "Think IT isn't for you",
    'course.recognize.c3.text': "Code, development, technology — sounds complicated. Feels like it's only for those who studied for years.",
    'course.recognize.c4.title': 'See others earning — and missing out',
    'course.recognize.c4.text': "AI has already changed the market. Those who got in early are already earning. You're still thinking.",
    'course.offer.title': 'What we offer?',
    'course.offer.tag1': 'Online course',
    'course.offer.tag2': 'For beginners',
    'course.offer.heading': 'AI Website Creator',
    'course.offer.description': 'A specialist who creates websites with AI — takes orders, earns on their own terms, and works from anywhere in the world.',
    'course.offer.tagline1': 'One skill that opens a new source of income.',
    'course.offer.tagline2': 'No experience, no code — just you and AI.',
    'course.offer.feature1': "We'll show you the tools we use to build websites",
    'course.offer.feature2': "We'll explain how everything works — no fluff",
    'course.offer.feature3': "We'll teach you to use AI so it works for you",
    'course.offer.feature4': 'After the session, you create websites yourself and get paid for it',
    'course.offer.price': '$99',
    'course.offer.priceLabel': '/ one-time',
    'course.offer.priceDescription': 'Everything you need to start creating and selling websites.',
    'course.offer.cta': 'Buy course',
    'course.calc.title': 'How much can you earn creating websites with AI?',
    'course.calc.priceLabel': 'How much are you willing to sell a website for?',
    'course.calc.sitesLabel': 'How many websites per month do you want to make?',
    'course.calc.potentialIncome': 'YOUR POTENTIAL INCOME',
    'course.calc.perMonth': '/ month',
    'course.calc.coursePrice': 'Course price',
    'course.calc.payback': 'Payback',
    'course.calc.paybackValue': 'From 1st website',
    'course.calc.netProfit': 'Net profit (1st month)',
    'course.testimonials.title': 'Student reviews',
    'course.testimonials.subtitle': 'What those who have already completed the session say',
    'course.faq.title': 'Frequently asked questions',
    'course.faq.subtitle': 'Everything you need to know about the course',
    'course.faq.q1': "I've never built websites — will I succeed?",
    'course.faq.a1': "That's exactly who we created this course for. AI does all the complex work — you just describe what you want. During the session, you'll create your first website from scratch and see for yourself that it works.",
    'course.faq.q2': 'Will $99 pay off?',
    'course.faq.a2': 'One website order — and the course has already paid for itself. Play with the calculator and see for yourself.',
    'course.faq.q3': 'Why a live session and not just a video course?',
    'course.faq.a3': 'Because you can watch videos for months and do nothing. In a live session, you create a website right during the call — you leave with results, not just theory.',
    'course.faq.q4': 'How quickly can I get my first order?',
    'course.faq.a4': 'Technically — right after the session you already know how to create websites. The rest depends only on you.',
    'course.faq.q5': 'Can I really earn from this?',
    'course.faq.a5': 'Yes — if you actually do it. AI has removed all technical barriers. Only one question remains — do you want it. Thousands of people are already earning from websites with AI. You can be next.',
    'course.faq.q6': 'What if I have questions after the session?',
    'course.faq.a6': "After the session, you get our contact and can write to us anytime. We're available — we answer questions and help you figure things out if something is unclear.",
    'course.fear.title1': "While you're doubting — ",
    'course.fear.title2': 'someone has already started their digital business.',
    'course.fear.f1.fear': "«I've never built websites»",
    'course.fear.f1.reality': 'Just describe what you want — AI will do everything itself',
    'course.fear.f2.fear': "«It's hard and takes a long time»",
    'course.fear.f2.reality': 'You will create your first website right after the first session',
    'course.fear.f3.fear': "«I don't know where to find clients»",
    'course.fear.f3.reality': 'Every restaurant, salon, doctor, shop — is your potential client',
    'course.fear.f4.fear': '«What if I fail»',
    'course.fear.f4.reality': "The mentor is nearby — we won't leave you until you succeed",
    'course.cta.title': 'Ready to start building?',
    'course.cta.subtitle': 'One session. One skill. Unlimited websites.',
    'course.cta.btn': 'Buy the course — $99',
    'course.cta.link': 'or book a free consultation →',
    'course.testimonials.label': 'Student Reviews',
    'course.testimonials.r1.name': 'Marina',
    'course.testimonials.r1.role': 'Freelancer, Kyiv',
    'course.testimonials.r1.text': 'I thought it was hard. Built my first site in one session and already found a client. The mentor explained everything clearly, no fluff.',
    'course.testimonials.r2.name': 'Alexey',
    'course.testimonials.r2.role': 'Small Business, Warsaw',
    'course.testimonials.r2.text': 'Wanted to build sites for my business. After the session, I made a site in one evening. Paid off the course immediately — saved on a developer.',
    'course.testimonials.r3.name': 'Dmitry',
    'course.testimonials.r3.role': 'No IT experience, Berlin',
    'course.testimonials.r3.text': 'Zero programming experience. After the session, I understood how it works and now build sites for clients. Worth every cent.'
  },
  ru: {
    // Navigation
    'nav.getStarted': 'Бесплатная консультация',
    'nav.home': 'Главная',
    'nav.services': 'Цены',
    'nav.howItWorks': 'Как это работает',
    'nav.referral': 'Реферальная программа',
    'nav.privacy': 'Политика конфиденциальности',
    'nav.terms': 'Условия использования',
    'nav.pricing': 'Цены',
    'nav.faq': 'Вопросы',
    'nav.testimonials': 'Отзывы',
    // Cookie Banner
    'cookie.title': 'Мы используем cookies',
    'cookie.description': 'Мы используем cookies для улучшения вашего опыта на сайте, анализа трафика и персонализации контента.',
    'cookie.accept': 'Принять все',
    'cookie.decline': 'Отклонить',
    'cookie.learnMore': 'Подробнее',
    // Hero - H1
    'hero.badge': 'Профессиональное веб-агентство',
    'hero.title': 'Сделаем сайт бесплатно.',
    'hero.titleHighlight': 'Платишь только $15/мес.',
    'hero.subtitle': 'Сначала макет — потом решение. Не понравится — уходишь без оплаты.',
    'hero.bullet1': 'Бесплатное превью сайта до оплаты',
    'hero.bullet2': '$15/мес — стоимость поддержки сайта, если вам всё подходит',
    'hero.bullet3': 'Работаем онлайн по всему миру',
    'hero.cta': 'Бесплатная консультация',
    'hero.referral': 'Реферальная программа',
    'hero.receipt.title': 'Ваш чек',
    'hero.receipt.design': 'Дизайн сайта',
    'hero.receipt.dev': 'Разработка',
    'hero.receipt.support': 'Поддержка та хостинг',
    'hero.receipt.total': 'Разом до оплати',
    'hero.receipt.save': 'ВИГОДА $800',
    'hero.pill.dev': '0$ за разработку',
    'hero.pill.host': 'Хостинг включено',
    'hero.pill.edit': 'Безлімітні правки',
    // Pricing - H2: Цены и условия
    'pricing.title': 'Цены',
    'pricing.titleHighlight': 'и условия',
    'pricing.subtitle': 'Сначала бесплатный макет. Оплата только если вам всё подходит.',
    // H3: Бесплатное демо сайта
    'pricing.demo.title': 'Бесплатный макет сайта',
    'pricing.demo.label': 'БЕСПЛАТНО',
    'pricing.demo.desc': 'Покажем ваш будущий сайт до оплаты.',
    'pricing.demo.b1': 'Макет под ваш бизнес',
    'pricing.demo.b2': 'Можно попросить правки',
    'pricing.demo.b3': 'Если не понравится — не платите',
    'pricing.demo.cta': 'Бесплатная консультация',
    // H3: Разработка сайта — $249 (разово)
    'pricing.launch.title': 'Ваш сайт под ключ',
    'pricing.launch.price': '$15',
    'pricing.launch.period': '/ месяц',
    'pricing.launch.desc': 'Разработка бесплатно. Платите только за то, чтобы сайт работал.',
    'pricing.launch.b1': 'Индивидуальный дизайн',
    'pricing.launch.b2': 'Адаптация под телефон',
    'pricing.launch.b3': 'Правки по запросу',
    'pricing.launch.b4': 'Форма заявок',
    'pricing.launch.cta': 'Бесплатная консультация',
    // How It Works - H2: Как это работает
    'how.title': 'Как это',
    'how.titleHighlight': 'работает',
    // H3 steps
    'how.step1.title': 'Расскажите о вашем бизнесе',
    'how.step1.note': 'Займёт 2 минуты — чем занимаетесь и какой сайт нужен',
    'how.step2.title': 'Мы делаем бесплатное демо сайта',
    'how.step2.note': 'Покажем дизайн и структуру. Пришлём ссылку в 1–3 дня',
    'how.step3.title': 'Вы говорите, что изменить',
    'how.step3.note': 'Текст, блоки, стиль — мы всё правим под вас',
    'how.step4.title': 'Подключаете $15/мес — и сайт работает',
    'how.step4.note': 'Разработка уже включена. Платите только за хостинг и поддержку',
    'how.step5.title': 'Подключаем домен и публикуем сайт',
    'how.step5.note': 'Сайт начинает работать и принимать заявки',
    'how.note': 'Если сайт вам не понравится — вы не платите.',
    // FAQ - H2: Частые вопросы
    'faq.title': 'Частые',
    'faq.titleHighlight': 'вопросы',
    'faq.subtitle': 'Всё, что нужно знать о работе с нами',
    // H3 questions
    'faq.q1': 'Почему сайт бесплатно?',
    'faq.a1': 'Мы зарабатываем на обслуживании, а не на разработке. Вы платите $15/мес — и нам выгодно делать сайты качественно, чтобы клиенты оставались надолго.',
    'faq.q2': 'Что входит в $15 в месяц?',
    'faq.a2': 'Хостинг, домен, техподдержка и правки по запросу. Сайт работает, принимает заявки — вы ни о чём не думаете.',
    'faq.q3': 'Я плачу $15 вечно?',
    'faq.a3': 'Да, пока сайт работает. Отменить можно в любой момент. Если отменяете — сайт отключается, но все материалы остаются у вас.',
    'faq.q4': 'Что если сайт мне не понравится?',
    'faq.a4': 'Просто уходите. Макет — бесплатный, правки — бесплатные. Платить начинаете только когда сами скажете "всё устраивает".',
    'faq.q5': 'Можно ли попросить правки после запуска?',
    'faq.a5': 'Да. Правки входят в $15/мес. Написали нам — исправили.',
    'faq.q6': 'Что я получаю на старте?',
    'faq.a6': 'Готовый сайт с индивидуальным дизайном, адаптацией под телефон и формой заявок. Подключённый домен и хостинг. Всё под ключ.',
    // Testimonials - H2: Отзывы клиентов
    'testimonials.title': 'Отзывы',
    'testimonials.titleHighlight': 'клиентов',
    'testimonials.subtitle': 'Что говорят клиенты о работе с нами',
    'testimonials.r1.name': 'Анастасия',
    'testimonials.r1.text': 'Понравилось, что сначала мне показали сайт бесплатно. Я попросила пару правок, всё быстро исправили. Подключила за $15 в месяц — и сайт сразу заработал.',
    'testimonials.r1.business': 'Мастер бровей',
    'testimonials.r2.name': 'Дмитрий',
    'testimonials.r2.text': 'Мне сделали понятный сайт под мои услуги. Сначала показали макет, потом внесли правки. Плачу $15 в месяц — и всё работает.',
    'testimonials.r2.business': 'Мастер по сантехнике',
    'testimonials.r3.name': 'Сергей',
    'testimonials.r3.text': 'Удобно, что не нужно платить заранее. Сначала посмотрел сайт, попросил упростить текст, после этого подключил за $15 в месяц. Всё честно и понятно.',
    // Consultation - H2: Получить бесплатное демо
    'consultation.title': 'Заказать бесплатную',
    'consultation.titleHighlight': 'консультацию',
    'consultation.subtitle': 'Расскажите, что вам нужно — мы пришлём ссылку на превью.',
    'consultation.benefit1': 'Бесплатный просмотр',
    'consultation.benefit2': 'Быстрая доставка',
    'consultation.benefit3': 'Без обязательств',
    'consultation.name': 'Имя',
    'consultation.namePlaceholder': 'Ваше имя',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Описание проекта',
    'consultation.descPlaceholder': 'Расскажите о вашем бизнесе и о том, какой сайт вам нужен…',
    'consultation.cta': 'Заказать бесплатную консультацию',
    'consultation.note': 'Без обязательств. Если сайт не понравится — вы не платите.',
    'consultation.sending': 'Отправка...',
    'consultation.or': 'или напишите нам напрямую',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Услуги',
    'footer.company': 'Компания',
    'footer.legal': 'Юридическое',
    'footer.contact': 'Контакты',
    'footer.rights': 'Все права защищены.',
    'footer.aboutLink': 'О компании',
    // Stats
    'stats.websites': 'Сайтов запущено',
    'stats.clients': 'Довольных клиентов',
    'stats.years': 'Лет опыта',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вклад',
    'stats.subtitle': 'Результаты для бизнеса по всему миру',
    // AI Course Page
    'course.hero.badge': 'Стань владельцем digital-бизнеса',
    'course.hero.title': 'Онлайн-курс по созданию сайтов с помощью ИИ',
    'course.hero.pill1': 'Без опыта',
    'course.hero.pill2': 'Быстро окупается',
    'course.hero.pill3': 'Работай на себя',
    'course.hero.socialProof': 'уже зарабатывают с ИИ',
    'course.hero.socialProofCount': '100+ студентов',
    'course.hero.specialPrice': 'Специальная цена',
    'course.hero.buyCourse': 'Купить курс',
    'course.recognize.title': 'Узнаёшь себя?',
    'course.recognize.c1.title': 'Работаешь на кого-то — а мог бы на себя',
    'course.recognize.c1.text': 'Каждый день отдаёшь своё время и силы чужому бизнесу. А свой — всё никак не начать.',
    'course.recognize.c2.title': 'Хочешь больше денег — но не знаешь как',
    'course.recognize.c2.text': 'Зарплата не растёт. Фриланс пугает. Кажется что для заработка в интернете нужны особые знания.',
    'course.recognize.c3.title': 'Думаешь что IT — это не для тебя',
    'course.recognize.c3.text': 'Код, разработка, технологии — звучит сложно. Кажется что это только для тех кто учился годами.',
    'course.recognize.c4.title': 'Видишь как другие зарабатывают — и упускаешь время',
    'course.recognize.c4.text': 'ИИ уже изменил рынок. Те кто зашёл раньше — уже зарабатывают. Ты всё ещё думаешь.',
    'course.offer.title': 'Что мы предлагаем?',
    'course.offer.tag1': 'Онлайн курс',
    'course.offer.tag2': 'Для новичков',
    'course.offer.heading': 'AI Website Creator',
    'course.offer.description': 'Специалист который создаёт сайты с помощью ИИ — берёт заказы, зарабатывает на своих условиях и работает из любой точки мира.',
    'course.offer.tagline1': 'Один навык который открывает новый источник дохода.',
    'course.offer.tagline2': 'Без опыта, без кода — просто ты и ИИ.',
    'course.offer.feature1': 'Покажем инструменты которыми делаем сайты сами',
    'course.offer.feature2': 'Разберём как всё устроено — без воды и лишнего',
    'course.offer.feature3': 'Научим пользоваться ИИ так чтобы он работал на тебя',
    'course.offer.feature4': 'После сессии ты сам создаёшь сайты и берёшь за это деньги',
    'course.offer.price': '$99',
    'course.offer.priceLabel': '/ единоразово',
    'course.offer.priceDescription': 'Всё, что нужно, чтобы начать создавать и продавать сайты.',
    'course.offer.cta': 'Купить курс',
    'course.calc.title': 'Сколько можно заработать на создании сайтов с помощью ИИ?',
    'course.calc.priceLabel': 'За сколько ты готов продать сайт?',
    'course.calc.sitesLabel': 'Сколько сайтов в месяц ты хочешь делать?',
    'course.calc.potentialIncome': 'ТВОЙ ПОТЕНЦИАЛЬНЫЙ ДОХОД',
    'course.calc.perMonth': '/ мес',
    'course.calc.coursePrice': 'Стоимость курса',
    'course.calc.payback': 'Окупаемость',
    'course.calc.paybackValue': 'С 1-го сайта',
    'course.calc.netProfit': 'Чистая прибыль (1-й мес)',
    'course.testimonials.title': 'Отзывы студентов',
    'course.testimonials.subtitle': 'Что говорят те, кто уже прошёл сессию',
    'course.faq.title': 'Частые вопросы',
    'course.faq.subtitle': 'Всё, что нужно знать о курсе',
    'course.faq.q1': 'Я никогда не делал сайты — у меня получится?',
    'course.faq.a1': 'Именно для таких мы и создали этот курс. ИИ делает всю сложную часть — ты просто описываешь что хочешь. На сессии ты создашь свой первый сайт с нуля и увидишь сам что это работает.',
    'course.faq.q2': 'Окупится ли $99?',
    'course.faq.a2': 'Один заказ на сайт — и курс уже окупился. Крути калькулятор и считай сам.',
    'course.faq.q3': 'Почему живая сессия а не просто видеокурс?',
    'course.faq.a3': 'Потому что видео можно смотреть месяцами и ничего не делать. На живой сессии ты создаёшь сайт прямо во время звонка — уходишь с результатом, а не с теорией.',
    'course.faq.q4': 'Как быстро я смогу взять первый заказ?',
    'course.faq.a4': 'Технически — сразу после сессии ты уже умеешь создавать сайты. Остальное зависит только от тебя.',
    'course.faq.q5': 'Я смогу зарабатывать на этом реально?',
    'course.faq.a5': 'Да — если будешь делать. ИИ убрал все технические барьеры. Остался только один вопрос — хочешь ли ты этого. Тысячи людей уже зарабатывают на сайтах с ИИ. Ты можешь быть следующим.',
    'course.faq.q6': 'Что если после сессии у меня останутся вопросы?',
    'course.faq.a6': 'После сессии ты получаешь наш контакт и можешь писать нам в любое время. Мы на связи — отвечаем на вопросы и помогаем разобраться если что-то непонятно.',
    'course.fear.title1': 'Пока ты сомневаешься — ',
    'course.fear.title2': 'кто-то уже открыл свой digital-бизнес.',
    'course.fear.f1.fear': '«Я никогда не делал сайты»',
    'course.fear.f1.reality': 'Просто опиши что хочешь — ИИ сделает всё сам',
    'course.fear.f2.fear': '«Это сложно и долго»',
    'course.fear.f2.reality': 'Ты создашь первый сайт прямо после первого занятия',
    'course.fear.f3.fear': '«Не знаю где брать клиентов»',
    'course.fear.f3.reality': 'Каждый ресторан, салон, врач, магазин — твой потенциальный клиент',
    'course.fear.f4.fear': '«А вдруг не получится»',
    'course.fear.f4.reality': 'Ментор рядом — не бросим пока не получится',
    'course.cta.title': 'Готовы начать создавать?',
    'course.cta.subtitle': 'Одна сессия. Один навык. Неограниченное количество сайтов.',
    'course.cta.btn': 'Купить курс — $99',
    'course.cta.link': 'или записаться на бесплатную консультацию →',
    'course.testimonials.label': 'Отзывы студентов',
    'course.testimonials.r1.name': 'Марина',
    'course.testimonials.r1.role': 'Фрилансер, Киев',
    'course.testimonials.r1.text': 'Думала, что это сложно. За одну сессию сделала первый сайт — и уже нашла клиента. Ментор объяснял всё понятно, без воды.',
    'course.testimonials.r2.name': 'Алексей',
    'course.testimonials.r2.role': 'Малый бизнес, Варшава',
    'course.testimonials.r2.text': 'Хотел сам делать сайты для своего бизнеса. После сессии сделал сайт за вечер. Окупил курс сразу — сэкономил на разработчике.',
    'course.testimonials.r3.name': 'Дмитрий',
    'course.testimonials.r3.role': 'Без опыта в IT, Берлин',
    'course.testimonials.r3.text': 'Ноль опыта в программировании. После сессии понял как всё работает и уже делаю сайты на заказ. Стоит каждого цента.'
  },
  ua: {
    // Navigation
    'nav.getStarted': 'Безкоштовна консультація',
    'nav.home': 'Головна',
    'nav.services': 'Ціни',
    'nav.howItWorks': 'Як це працює',
    'nav.referral': 'Реферальна програма',
    'nav.privacy': 'Політика конфіденційності',
    'nav.terms': 'Умови використання',
    'nav.pricing': 'Ціни',
    'nav.faq': 'Питання',
    'nav.testimonials': 'Відгуки',
    // Cookie Banner
    'cookie.title': 'Ми використовуємо cookies',
    'cookie.description': 'Ми використовуємо cookies для покращення вашого досвіду на сайті, аналізу трафіку та персоналізації контенту.',
    'cookie.accept': 'Прийняти все',
    'cookie.decline': 'Відхилити',
    'cookie.learnMore': 'Детальніше',
    // Hero - H1
    'hero.badge': 'Професійне веб-агентство',
    'hero.title': 'Зробимо сайт безкоштовно.',
    'hero.titleHighlight': 'Платиш лише $15/міс.',
    'hero.subtitle': 'Спочатку макет — потім рішення. Не сподобається — йдеш без оплати.',
    'hero.bullet1': "Безкоштовне прев'ю сайту до оплати",
    'hero.bullet2': '$15/міс — вартість підтримки сайту, якщо вам усе підходить',
    'hero.bullet3': 'Працюємо онлайн по всьому світу',
    'hero.cta': 'Безкоштовна консультація',
    'hero.referral': 'Реферальна програма',
    'hero.receipt.title': 'Ваша квитанція',
    'hero.receipt.design': 'Дизайн сайту',
    'hero.receipt.dev': 'Розробка',
    'hero.receipt.support': 'Підтримка та хостинг',
    'hero.receipt.total': 'Усього',
    'hero.receipt.save': 'ЗАХОДИТЬ $800',
    'hero.pill.dev': 'Розробка безкоштовно',
    'hero.pill.host': 'Хостинг включено',
    'hero.pill.edit': 'Безлімітні правки',
    // Pricing - H2: Ціни та умови
    'pricing.title': 'Ціни',
    'pricing.titleHighlight': 'та умови',
    'pricing.subtitle': 'Спочатку безкоштовний макет. Оплата тільки якщо вам усе підходить.',
    // H3: Безкоштовне демо сайту
    'pricing.demo.title': 'Безкоштовний макет сайту',
    'pricing.demo.label': 'БЕЗКОШТОВНО',
    'pricing.demo.desc': 'Покажемо ваш майбутній сайт до оплати.',
    'pricing.demo.b1': 'Макет під ваш бізнес',
    'pricing.demo.b2': 'Можна попросити правки',
    'pricing.demo.b3': 'Якщо не сподобається — не платите',
    'pricing.demo.cta': 'Безкоштовна консультація',
    // H3: Розробка сайту — $249 (разово)
    'pricing.launch.title': 'Ваш сайт під ключ',
    'pricing.launch.price': '$15',
    'pricing.launch.period': '/ місяць',
    'pricing.launch.desc': 'Розробка безкоштовно. Платите лише за те, щоб сайт працював.',
    'pricing.launch.b1': 'Індивідуальний дизайн',
    'pricing.launch.b2': 'Адаптація під телефон',
    'pricing.launch.b3': 'Правки за запитом',
    'pricing.launch.b4': 'Форма заявок',
    'pricing.launch.cta': 'Безкоштовна консультація',
    // How It Works - H2: Як це працює
    'how.title': 'Як це',
    'how.titleHighlight': 'працює',
    // H3 steps
    'how.step1.title': 'Розкажіть про ваш бізнес',
    'how.step1.note': 'Займе 2 хвилини — чим займаєтесь і який сайт потрібен',
    'how.step2.title': 'Ми робимо безкоштовне демо сайту',
    'how.step2.note': 'Покажемо дизайн і структуру. Надішлемо посилання за 1–3 дні',
    'how.step3.title': 'Ви кажете, що змінити',
    'how.step3.note': 'Текст, блоки, стиль — ми все підправляємо під вас',
    'how.step4.title': 'Підключаєте $15/міс — і сайт працює',
    'how.step4.note': 'Розробка вже включена. Платите лише за хостинг та підтримку',
    'how.step5.title': 'Підключаємо домен і публікуємо сайт',
    'how.step5.note': 'Сайт починає працювати та приймати заявки',
    'how.note': 'Якщо сайт вам не сподобається — ви не платите.',
    // FAQ - H2: Поширені запитання
    'faq.title': 'Поширені',
    'faq.titleHighlight': 'запитання',
    'faq.subtitle': 'Все, що потрібно знати про роботу з нами',
    // H3 questions
    'faq.q1': 'Чому сайт безкоштовно?',
    'faq.a1': 'Ми заробляємо на обслуговуванні, а не на розробці. Ви платите $15/міс — і нам вигідно робити сайти якісно, щоб клієнти залишалися надовго.',
    'faq.q2': 'Що входить у $15 на місяць?',
    'faq.a2': 'Хостинг, домен, техпідтримка та правки за запитом. Сайт працює, приймає заявки — ви ні про що не думаєте.',
    'faq.q3': 'Я плачу $15 вічно?',
    'faq.a3': 'Так, поки сайт працює. Скасувати можна в будь-який момент. Якщо скасовуєте — сайт вимикається, але всі матеріали залишаються у вас.',
    'faq.q4': 'Що якщо сайт мені не сподобається?',
    'faq.a4': 'Просто йдете. Макет — безкоштовний, правки — безкоштовні. Платити починаєте тільки коли самі скажете "все влаштовує".',
    'faq.q5': 'Чи можна попросити правки після запуску?',
    'faq.a5': 'Так. Правки входять у $15/міс. Написали нам — виправили.',
    'faq.q6': 'Що я отримую на старті?',
    'faq.a6': 'Готовий сайт з індивідуальним дизайном, адаптацією під телефон та формою заявок. Підключений домен і хостинг. Все під ключ.',
    // Testimonials - H2: Відгуки клієнтів
    'testimonials.title': 'Відгуки',
    'testimonials.titleHighlight': 'клієнтів',
    'testimonials.subtitle': 'Що кажуть клієнти про роботу з нами',
    'testimonials.r1.name': 'Оксана',
    'testimonials.r1.text': 'Сподобалося, що спочатку мені показали сайт безкоштовно. Я попросила пару правок, все швидко виправили. Підключила за $15 на місяць — і сайт відразу запрацював.',
    'testimonials.r1.business': 'Майстер манікюру',
    'testimonials.r2.name': 'Віталій',
    'testimonials.r2.text': 'Мені зробили зрозумілий сайт під мої послуги. Спочатку показали макет, потім внесли правки. Плачу $15 на місяць — і все працює.',
    'testimonials.r2.business': 'Ремонт техніки',
    'testimonials.r3.name': 'Андрій',
    'testimonials.r3.text': 'Зручно, що не потрібно платити заздалегідь. Спочатку подивився сайт, попросив спростити текст, після цього підключив за $15 на місяць. Все чесно і зрозуміло.',
    // Consultation - H2: Отримати безкоштовне демо
    'consultation.title': 'Замовити безкоштовну',
    'consultation.titleHighlight': 'консультацію',
    'consultation.subtitle': "Скажіть, що вам потрібно — ми надішлемо посилання на прев'ю.",
    'consultation.benefit1': 'Безкоштовний перегляд',
    'consultation.benefit2': 'Швидка доставка',
    'consultation.benefit3': "Без зобов'язань",
    'consultation.name': "Ім'я",
    'consultation.namePlaceholder': "Ваше ім'я",
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Опис проєкту',
    'consultation.descPlaceholder': 'Розкажіть про ваш бізнес і який сайт вам потрібен...',
    'consultation.cta': 'Замовити безкоштовну консультацію',
    'consultation.note': "Без зобов'язань. Якщо сайт не сподобається — ви не платите.",
    'consultation.sending': 'Відправка...',
    'consultation.or': 'або напишіть нам напряму',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Послуги',
    'footer.company': 'Компанія',
    'footer.legal': 'Юридичне',
    'footer.contact': 'Контакти',
    'footer.rights': 'Всі права захищені.',
    'footer.aboutLink': 'Про нас',
    // Stats
    'stats.websites': 'Сайтів запущено',
    'stats.clients': 'Задоволених клієнтів',
    'stats.years': 'Років досвіду',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вплив',
    'stats.subtitle': 'Результати для бізнесу по всьому світу',
    // AI Course Page
    'course.hero.badge': 'Стань власником digital-бізнесу',
    'course.hero.title': 'Онлайн-курс зі створення сайтів за допомогою ШІ',
    'course.hero.pill1': 'Без досвіду',
    'course.hero.pill2': 'Швидко окупається',
    'course.hero.pill3': 'Працюй на себе',
    'course.hero.socialProof': 'вже заробляють з ШІ',
    'course.hero.socialProofCount': '100+ студентів',
    'course.hero.specialPrice': 'Спеціальна ціна',
    'course.hero.buyCourse': 'Купити курс',
    'course.recognize.title': 'Впізнаєш себе?',
    'course.recognize.c1.title': 'Працюєш на когось — а міг би на себе',
    'course.recognize.c1.text': 'Кожен день віддаєш свій час і сили чужому бізнесу. А свій — все ніяк не почати.',
    'course.recognize.c2.title': 'Хочеш більше грошей — але не знаєш як',
    'course.recognize.c2.text': 'Зарплата не росте. Фріланс лякає. Здається, що для заробітку в інтернеті потрібні особливі знання.',
    'course.recognize.c3.title': 'Думаєш що IT — це не для тебе',
    'course.recognize.c3.text': 'Код, розробка, технології — звучить складно. Здається, що це тільки для тих, хто вчився роками.',
    'course.recognize.c4.title': 'Бачиш як інші заробляють — і втрачаєш час',
    'course.recognize.c4.text': 'ШІ вже змінив ринок. Ті, хто зайшов раніше — вже заробляють. Ти все ще думаєш.',
    'course.offer.title': 'Що ми пропонуємо?',
    'course.offer.tag1': 'Онлайн курс',
    'course.offer.tag2': 'Для новачків',
    'course.offer.heading': 'AI Website Creator',
    'course.offer.description': 'Спеціаліст, який створює сайти за допомогою ШІ — бере замовлення, заробляє на своїх умовах і працює з будь-якої точки світу.',
    'course.offer.tagline1': 'Одна навичка, яка відкриває нове джерело доходу.',
    'course.offer.tagline2': 'Без досвіду, без коду — просто ти і ШІ.',
    'course.offer.feature1': 'Покажемо інструменти, якими робимо сайти самі',
    'course.offer.feature2': 'Розберемо як все влаштовано — без води і зайвого',
    'course.offer.feature3': 'Навчимо користуватися ШІ так, щоб він працював на тебе',
    'course.offer.feature4': 'Після сесії ти сам створюєш сайти і береш за це гроші',
    'course.offer.price': '$99',
    'course.offer.priceLabel': '/ одноразово',
    'course.offer.priceDescription': 'Все, що потрібно, щоб почати створювати і продавати сайти.',
    'course.offer.cta': 'Купити курс',
    'course.calc.title': 'Скільки можна заробити на створенні сайтів за допомогою ШІ?',
    'course.calc.priceLabel': 'За скільки ти готовий продати сайт?',
    'course.calc.sitesLabel': 'Скільки сайтів на місяць ти хочеш робити?',
    'course.calc.potentialIncome': 'ТВІЙ ПОТЕНЦІЙНИЙ ДОХІД',
    'course.calc.perMonth': '/ міс',
    'course.calc.coursePrice': 'Вартість курсу',
    'course.calc.payback': 'Окупність',
    'course.calc.paybackValue': 'З 1-го сайту',
    'course.calc.netProfit': 'Чистий прибуток (1-й міс)',
    'course.testimonials.title': 'Відгуки студентів',
    'course.testimonials.subtitle': 'Що кажуть ті, хто вже пройшов сесію',
    'course.faq.title': 'Часті питання',
    'course.faq.subtitle': 'Все, що потрібно знати про курс',
    'course.faq.q1': 'Я ніколи не робив сайти — у мене вийде?',
    'course.faq.a1': 'Саме для таких ми і створили цей курс. ШІ робить всю складну частину — ти просто описуєш що хочеш. На сесії ти створиш свій перший сайт з нуля і побачиш сам, що це працює.',
    'course.faq.q2': 'Чи окупиться $99?',
    'course.faq.a2': 'Одне замовлення на сайт — і курс вже окупився. Крути калькулятор і рахуй сам.',
    'course.faq.q3': 'Чому жива сесія, а не просто відеокурс?',
    'course.faq.a3': 'Тому що відео можна дивитися місяцями і нічого не робити. На живій сесії ти створюєш сайт прямо під час дзвінка — йдеш з результатом, а не з теорією.',
    'course.faq.q4': 'Як швидко я зможу взяти перше замовлення?',
    'course.faq.a4': 'Технічно — відразу після сесії ти вже вмієш створювати сайти. Решта залежить тільки від тебе.',
    'course.faq.q5': 'Я зможу заробляти на цьому реально?',
    'course.faq.a5': "Так — якщо будеш робити. ШІ прибрав всі технічні бар'єри. Залишилося тільки одне питання — чи хочеш ти цього. Тисячі людей вже заробляють на сайтах з ШІ. Ти можеш бути наступним.",
    'course.faq.q6': 'Що якщо після сесії у мене залишаться питання?',
    'course.faq.a6': "Після сесії ти отримуєш наш контакт і можеш писати нам в будь-який час. Ми на зв'язку — відповідаємо на питання і допомагаємо розібратися, якщо щось незрозуміло.",
    'course.fear.title1': 'Поки ти сумніваєшся — ',
    'course.fear.title2': 'хтось вже відкрив свій digital-бізнес.',
    'course.fear.f1.fear': '«Я ніколи не робив сайти»',
    'course.fear.f1.reality': 'Просто опиши що хочеш — ШІ зробить все сам',
    'course.fear.f2.fear': '«Це складно і довго»',
    'course.fear.f2.reality': 'Ти створиш перший сайт прямо після першого заняття',
    'course.fear.f3.fear': '«Не знаю де брати клієнтів»',
    'course.fear.f3.reality': 'Кожен ресторан, салон, лікар, магазин — твій потенційний клієнт',
    'course.fear.f4.fear': '«А раптом не вийде»',
    'course.fear.f4.reality': 'Ментор поруч — не кинемо поки не вийде',
    'course.cta.title': 'Готові почати створювати?',
    'course.cta.subtitle': 'Одна сесія. Одна навичка. Необмежена кількість сайтів.',
    'course.cta.btn': 'Купити курс — $99',
    'course.cta.link': 'або записатися на безкоштовну консультацію →',
    'course.testimonials.label': 'Відгуки студентів',
    'course.testimonials.r1.name': 'Марина',
    'course.testimonials.r1.role': 'Фрілансер, Київ',
    'course.testimonials.r1.text': 'Думала, що це складно. За одну сесію зробила перший сайт — і вже знайшла клієнта. Ментор пояснював все зрозуміло, без води.',
    'course.testimonials.r2.name': 'Олексій',
    'course.testimonials.r2.role': 'Малий бізнес, Варшава',
    'course.testimonials.r2.text': 'Хотів сам робити сайти для свого бізнесу. Після сесії зробив сайт за вечір. Окупив курс відразу — заощадив на розробнику.',
    'course.testimonials.r3.name': 'Дмитро',
    'course.testimonials.r3.role': 'Без досвіду в IT, Берлін',
    'course.testimonials.r3.text': 'Нуль досвіду в програмуванні. Після сесії зрозумів як все працює і вже роблю сайти на замовлення. Вартує кожного цента.'
  }
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/ru')) {
      setLanguage('ru');
    } else if (path.startsWith('/ua')) {
      setLanguage('ua');
    } else if (path.startsWith('/uk')) {
      setLanguage('ua');
    } else {
      setLanguage('en');
    }
  }, []);
  const t = (key: string): string => {
    return translations[language][key as keyof (typeof translations)['en']] || key;
  };
  const getPath = (path: string): string => {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    if (language === 'ru') {
      return `/ru/${cleanPath}`;
    } else if (language === 'ua') {
      return `/ua/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    getPath
  }} data-id="element-1198">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}